import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

type Rect = { left: number; top: number; width: number; height: number };

type Gesture = {
  pointerId: number;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  boardScrollLeft: number;
  column: HTMLElement | null;
  columnScrollTop: number;
  base: Set<string>;
  additive: boolean;
  active: boolean;
};

const INTERACTIVE = "[data-task-id], button, a, input, textarea, select, [role='button'], [contenteditable='true']";

/** Select only rendered cards intersecting a background-origin rectangle. */
export function useMarqueeSelection(
  boardRef: React.RefObject<HTMLElement | null>,
  selected: ReadonlySet<string>,
  onChange: (ids: Set<string>) => void,
) {
  const [rectangle, setRectangle] = useState<Rect | null>(null);
  const gestureRef = useRef<Gesture | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const hitTest = useCallback(() => {
    const gesture = gestureRef.current;
    const board = boardRef.current;
    if (!gesture || !gesture.active || !board) return;
    // Keep the origin attached to board/column content as either scroller moves.
    const startX = gesture.startX - (board.scrollLeft - gesture.boardScrollLeft);
    const startY = gesture.startY - ((gesture.column?.scrollTop ?? 0) - gesture.columnScrollTop);
    const left = Math.min(startX, gesture.currentX);
    const top = Math.min(startY, gesture.currentY);
    const right = Math.max(startX, gesture.currentX);
    const bottom = Math.max(startY, gesture.currentY);
    setRectangle({ left, top, width: right - left, height: bottom - top });
    const next = new Set(gesture.additive ? gesture.base : []);
    board.querySelectorAll<HTMLElement>("[data-task-id]").forEach((card) => {
      const bounds = card.getBoundingClientRect();
      const lane = card.closest<HTMLElement>("[data-testid^='kanban-column-items-']");
      const clip = lane?.getBoundingClientRect();
      const visibleLeft = clip?.width ? Math.max(bounds.left, clip.left) : bounds.left;
      const visibleRight = clip?.width ? Math.min(bounds.right, clip.right) : bounds.right;
      const visibleTop = clip?.height ? Math.max(bounds.top, clip.top) : bounds.top;
      const visibleBottom = clip?.height ? Math.min(bounds.bottom, clip.bottom) : bounds.bottom;
      if (visibleLeft < right && visibleRight > left && visibleTop < bottom && visibleBottom > top) {
        const id = card.dataset.taskId;
        if (id) next.add(id);
      }
    });
    onChangeRef.current(next);
  }, [boardRef]);

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      const gesture = gestureRef.current;
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      event.preventDefault();
      gesture.currentX = event.clientX;
      gesture.currentY = event.clientY;
      if (!gesture.active && Math.hypot(event.clientX - gesture.startX, event.clientY - gesture.startY) >= 6) {
        gesture.active = true;
      }
      hitTest();
    };
    const onUp = (event: PointerEvent) => {
      if (gestureRef.current?.pointerId !== event.pointerId) return;
      gestureRef.current = null;
      setRectangle(null);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || !gestureRef.current) return;
      onChangeRef.current(new Set(gestureRef.current.base));
      gestureRef.current = null;
      setRectangle(null);
    };
    const onScroll = () => { hitTest(); };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    window.addEventListener("keydown", onKey);
    document.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("scroll", onScroll, true);
    };
  }, [hitTest]);

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0 || !(event.target instanceof Element)) return;
    if (event.target.closest(INTERACTIVE)) return;
    event.preventDefault();
    window.getSelection()?.removeAllRanges();
    gestureRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      currentX: event.clientX,
      currentY: event.clientY,
      boardScrollLeft: boardRef.current?.scrollLeft ?? 0,
      column: event.target.closest<HTMLElement>("[data-testid^='kanban-column-items-']"),
      columnScrollTop: event.target.closest<HTMLElement>("[data-testid^='kanban-column-items-']")?.scrollTop ?? 0,
      base: new Set(selected),
      additive: event.ctrlKey || event.metaKey || event.shiftKey,
      active: false,
    };
  }, [selected]);

  return { rectangle, onPointerDown };
}
